import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Store,
  ShoppingCart,
  Bot,
  Link as LinkIcon,
  BarChart3,
  CheckCircle,
  Layers3,
  Target,
  Zap,
  ShieldCheck,
  TrendingUp
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

export default function Product() {
  const { language } = useLanguage();

  const pillars = [
    {
      icon: Store,
      color: 'cyan',
      title: language === 'pt' ? '1. Fundação de Alta Conversão' : '1. High-Converting Foundation',
      description:
        language === 'pt'
          ? 'Tiramos sua ideia do papel com uma loja estruturada e pronta para vender. Criamos as páginas essenciais, organizamos o catálogo e garantimos a base técnica para você operar com segurança total.'
          : 'We bring your idea to life with a structured store ready to sell. We create essential pages, organize the catalog, and ensure the technical foundation for you to operate with total security.',
      bullets:
        language === 'pt'
          ? ['Estrutura técnica voltada para conversão', 'Configuração de páginas e produtos', 'Base sólida pronta para receber integrações futuras']
          : ['Technical structure focused on conversion', 'Page and product configuration', 'Solid base ready for future integrations'],
    },
    {
      icon: ShoppingCart,
      color: 'emerald',
      title: language === 'pt' ? '2. Máquina de Recuperação de Receita' : '2. Revenue Recovery Machine',
      description:
        language === 'pt'
          ? 'Com a loja rodando, ativamos automações silenciosas e precisas para recuperar carrinhos abandonados e reengajar clientes que pararam de comprar.'
          : 'With the store running, we activate silent and precise automations to recover abandoned carts and re-engage customers who stopped buying.',
      bullets:
        language === 'pt'
          ? ['Modelo de sucesso (success fee) para cenários elegíveis', 'Mensagens estratégicas com regras de contato claras', 'Geração de lucro usando a sua base atual de contatos']
          : ['Success-fee model for eligible scenarios', 'Strategic messaging with clear contact rules', 'Profit generation using your current contact base'],
    },
    {
      icon: Bot,
      color: 'purple',
      title: language === 'pt' ? '3. Escala com Atendimento IA 24/7' : '3. Scale with 24/7 AI Support',
      description:
        language === 'pt'
          ? 'Quando o volume de dúvidas trava a sua equipe, implementamos Inteligência Artificial para responder perguntas repetitivas e fazer triagem inteligente em tempo real.'
          : 'When the volume of questions bottlenecks your team, we implement Artificial Intelligence to answer repetitive queries and perform smart triage in real time.',
      bullets:
        language === 'pt'
          ? ['Integração com as ferramentas que você já usa', 'Criação de estruturas próprias (self-hosted) se necessário', 'Transbordo humano para casos complexos e delicados']
          : ['Integration with the tools you already use', 'Creation of self-hosted structures if necessary', 'Human handover for complex and sensitive cases'],
    },
    {
      icon: LinkIcon,
      color: 'amber',
      title: language === 'pt' ? '4. Rotina no Piloto Automático' : '4. Routine on Autopilot',
      description:
        language === 'pt'
          ? 'A ProcessSurge vira o motor invisível da sua empresa. Conectamos sua loja com CRM, ERP, logística e sistemas de atendimento para que os dados fluam sem trabalho manual.'
          : 'ProcessSurge becomes the invisible engine of your company. We connect your store with CRM, ERP, logistics, and support systems so data flows without manual work.',
      bullets:
        language === 'pt'
          ? ['Fim drástico do retrabalho e de tarefas repetitivas', 'Processos confiáveis que não dependem de memória humana', 'Sua operação leve e pronta para multiplicar de tamanho']
          : ['Drastic end to rework and repetitive tasks', 'Reliable processes that do not rely on human memory', 'Your operation lean and ready to multiply in size'],
    },
    {
      icon: BarChart3,
      color: 'blue',
      title: language === 'pt' ? '5. Raio-X da Operação e Lucro' : '5. Operational and Profit X-Ray',
      description:
        language === 'pt'
          ? 'Consolidamos todos os números em um só lugar. Entregamos painéis visuais para você acompanhar exatamente onde o negócio cresce, onde perde dinheiro e qual é o próximo passo.'
          : 'We consolidate all numbers in one place. We deliver visual dashboards so you can track exactly where the business is growing, where it is losing money, and what the next step is.',
      bullets:
        language === 'pt'
          ? ['Dashboards práticos para o dia a dia e para a diretoria', 'Relatórios claros sobre ganhos gerados por automação', 'Base de dados confiável para tomadas de decisão rápidas']
          : ['Practical dashboards for day-to-day and executive use', 'Clear reports on gains generated by automation', 'Reliable database for quick decision making'],
    },
  ];

  const colorMap = {
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  };

  return (
    <div className="relative">
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-950" />
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/70 border border-slate-800 text-cyan-400 font-medium text-sm mb-6 shadow-sm">
            <Layers3 className="w-4 h-4" />
            {language === 'pt' ? 'O Ecossistema ProcessSurge' : 'The ProcessSurge Ecosystem'}
          </div>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-100 leading-tight tracking-tight mb-6 max-w-4xl mx-auto">
            {language === 'pt' 
              ? 'Um ecossistema que cresce no ritmo exato do seu negócio' 
              : 'An ecosystem that grows at the exact pace of your business'}
          </h1>
          <p className="text-lg lg:text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto mb-10">
            {language === 'pt'
              ? 'Sistemas gigantes e engessados cobram por ferramentas que você nunca vai usar. Nós montamos a base perfeita para o seu estágio atual e conectamos novas engrenagens apenas quando você precisa escalar.'
              : 'Giant, rigid systems charge for tools you will never use. We build the perfect foundation for your current stage and connect new gears only when you need to scale.'}
          </p>
          <Link to={`/${language}/contact`}>
            <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-bold rounded-full px-8 py-6 text-lg">
              {language === 'pt' ? 'Descobrir por onde começar' : 'Find out where to start'}
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-20 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-100 mb-4">
              {language === 'pt' ? 'Você investe onde mais dói hoje' : 'You invest where it hurts the most today'}
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed">
              {language === 'pt'
                ? 'Ninguém precisa de tudo ao mesmo tempo. Você escolhe o módulo que ataca o seu maior gargalo agora. Assim que ele estiver resolvido e gerando lucro, avançamos juntos para a próxima etapa.'
                : 'No one needs everything at the same time. You choose the module that attacks your biggest bottleneck now. Once it is resolved and generating profit, we move to the next stage together.'}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {pillars.map((pillar, index) => (
              <div
                key={index}
                className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-10 hover:border-slate-700 transition-colors group"
              >
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold mb-6 ${colorMap[pillar.color]}`}>
                  <pillar.icon className="w-4 h-4" />
                  {pillar.title}
                </div>

                <p className="text-slate-300 leading-relaxed mb-6">{pillar.description}</p>

                <ul className="space-y-3">
                  {pillar.bullets.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3 text-slate-400">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900/30 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-100 mb-6">
                {language === 'pt' ? 'Por que um ecossistema e não um pacote fechado?' : 'Why an ecosystem and not a closed package?'}
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-6">
                {language === 'pt'
                  ? 'A maioria das agências e grandes plataformas tenta encaixar a sua empresa dentro do molde rígido deles. Nós fazemos o oposto. Preenchemos as lacunas de tecnologia e integração que hoje travam a sua operação de e-commerce.'
                  : 'Most agencies and large platforms try to fit your company into their rigid mold. We do the opposite. We fill the technology and integration gaps that currently slow down your e-commerce operation.'}
              </p>
              <p className="text-slate-400 text-lg leading-relaxed">
                {language === 'pt'
                  ? 'Quando faz sentido aproveitar um sistema que você já tem, nós integramos. Quando algo precisa ser construído do zero para ser mais leve e barato, nós desenvolvemos. É tecnologia que funciona a favor do seu bolso.'
                  : 'When it makes sense to use a system you already have, we integrate it. When something needs to be built from scratch to be lighter and cheaper, we develop it. It is technology that works in favor of your wallet.'}
              </p>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8">
              <h3 className="text-slate-100 font-bold text-xl mb-6">
                {language === 'pt' ? 'As vantagens da nossa abordagem' : 'The advantages of our approach'}
              </h3>
              <div className="space-y-5 text-slate-300">
                <div className="flex gap-4">
                  <Target className="w-6 h-6 text-cyan-400 shrink-0" />
                  <p>{language === 'pt' ? 'Foco absoluto no problema atual sem gastar com o que não precisa agora.' : 'Absolute focus on the current problem without spending on what is not needed right now.'}</p>
                </div>
                <div className="flex gap-4">
                  <Zap className="w-6 h-6 text-cyan-400 shrink-0" />
                  <p>{language === 'pt' ? 'Implementação mais ágil já que trabalhamos por etapas muito bem definidas.' : 'More agile implementation since we work in clearly defined stages.'}</p>
                </div>
                <div className="flex gap-4">
                  <TrendingUp className="w-6 h-6 text-cyan-400 shrink-0" />
                  <p>{language === 'pt' ? 'Custo alinhado ao seu momento de negócio permitindo reinvestimento do lucro.' : 'Cost aligned with your business stage allowing the reinvestment of profits.'}</p>
                </div>
                <div className="flex gap-4">
                  <ShieldCheck className="w-6 h-6 text-cyan-400 shrink-0" />
                  <p>{language === 'pt' ? 'Uma parceria técnica real de ponta a ponta que acompanha a sua evolução.' : 'A true end-to-end technical partnership that follows your evolution.'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950 text-center border-t border-slate-900">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-100 mb-4">
            {language === 'pt' ? 'Vamos destravar o seu próximo gargalo?' : 'Shall we unlock your next bottleneck?'}
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            {language === 'pt'
              ? 'Você conta o que está segurando as suas vendas ou tomando o seu tempo e nós montamos a solução exata.'
              : 'You tell us what is holding back your sales or taking up your time and we build the exact solution.'}
          </p>
          <Link to={`/${language}/contact`}>
            <Button className="bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-slate-950 font-bold rounded-full px-10 py-6 text-lg shadow-lg shadow-cyan-500/20">
              {language === 'pt' ? 'Falar com a ProcessSurge' : 'Talk to ProcessSurge'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
